import React from 'react';
import { Box, ButtonBase, Chip, Collapse, Link, Tooltip, Typography } from '@mui/material';
import OfferPortalApi, { OfferRecord } from '../../api/OfferPortalApi';
import OfferSummary from '../CaseOfferPanel/OfferSummary';
import {
  JOURNEY_OUTCOMES,
  JOURNEY_PHASES,
  JOURNEY_STEP_COUNT,
  JOURNEY_TRACKED_COUNT,
  JourneyBranch,
  JourneyFactKey,
  JourneyFacts,
  JourneyStamps,
  JourneyStep,
  STAMPABLE_FACTS,
  outcomesTaken,
} from './caseJourney';
import JourneyStampDialog from './JourneyStampDialog';

/**
 * Lloyd's 8-stage case progression (Client view _ Raza.docx):
 *   (1) pending payment → (2) LOR sent → (3) LOR confirmed → (4) Pre-trial scheduled →
 *   (5) Offer received / sent → (6) Offer accepted → (7) Deferral / DSC conditions → (8) closed
 *
 * These eight are the ONLY stages the system actually tracks, and everything downstream depends on
 * their indices — the open/closed filter, "Cases Needing Action", deriveStageIndex. They are
 * deliberately unchanged by the Aug 18 expansion; see caseJourney.ts, which wraps them in the wider
 * map Lloyd drew without moving any of them.
 */
export const CASE_STAGES: { short: string; full: string }[] = [
  { short: 'Pending', full: '1. Pending payment' },
  { short: 'LOR sent', full: '2. LOR sent' },
  { short: 'LOR confirmed', full: '3. LOR confirmed' },
  { short: 'Pre-trial', full: '4. Pre-trial scheduled' },
  { short: 'Offer', full: '5. Offer received / sent to client' },
  { short: 'Accepted', full: '6. Offer accepted (pending docs) / rejected / trial / appeal' },
  { short: 'Conditions', full: '7. Deferral / DSC conditions (affidavit / driving record)' },
  { short: 'Closed', full: '8. Closed' },
];

// Map our internal TicketStatus names onto Lloyd's 8 stages (0-indexed). Best-effort;
// stages not directly represented by a status (e.g. Conditions) are still shown in the bar.
const STATUS_TO_STAGE: Record<string, number> = {
  'citation received (Approving)': 0,
  'case accepted (Approved)': 0,
  'letter of representation sent (Active)': 1,
  'court respond by date satisfied (Pending)': 2,
  'New court date': 3,
  'negotiating with prosecutor': 4,
  'offer received': 4,
  'waiting for client to reject or accept offer': 4,
  'waiting for judge to approve': 5,
  'deferral/DSC conditions': 6,
  'case closed/dismissed (closed)': 7,
};

// Reverse map: the canonical internal status to write when a manager clicks a stage to move a
// case there. Stage 6 (Deferral / DSC conditions) is set automatically from an offer when it
// contains the trigger phrases (G.6), and staff can also set/override it here.
const STAGE_TO_STATUS: Record<number, string> = {
  0: 'case accepted (Approved)',
  1: 'letter of representation sent (Active)',
  2: 'court respond by date satisfied (Pending)',
  3: 'New court date',
  4: 'offer received',
  5: 'waiting for judge to approve',
  6: 'deferral/DSC conditions',
  7: 'case closed/dismissed (closed)',
};

export const CLOSED_STAGE_INDEX = 7;

/** "5. Offer received / sent to client" — the stage the OF box sits on. */
export const OFFER_STAGE_INDEX = 4;

export function stageForStatus(statusName: string | null | undefined): number {
  if (!statusName) return -1;
  return statusName in STATUS_TO_STAGE ? STATUS_TO_STAGE[statusName] : -1;
}

export function statusForStage(stageIndex: number): string | null {
  return STAGE_TO_STATUS[stageIndex] ?? null;
}

interface CaseStatusTimelineProps {
  statusName: string | null | undefined;
  /**
   * Override for the highlighted stage. Callers pass the LOR-flag-aware index
   * (see deriveStageIndex) so imported / no-status cases still light up a stage
   * instead of rendering an all-grey bar. Falls back to statusName mapping when omitted.
   */
  currentStageIndex?: number;
  /**
   * When provided, settable stages become clickable and call this with the canonical
   * internal status name + the stage's full label. Omit for a read-only bar.
   */
  onSelectStage?: (statusName: string, stageLabel: string) => void;
  /** Disables interaction (e.g. while a status change is in flight). */
  disabled?: boolean;
  /**
   * Traffic or non-traffic (Lloyd, Aug 6). Null on cases that pre-date the field, which are traffic.
   *
   * The stages describe a traffic case: LOR sent, LOR confirmed, pre-trial, deferral. A county
   * or district criminal matter passes through none of them, so showing the bar would invite staff
   * to move a case to a stage that does not exist for it.
   */
  matterType?: string | null;
  /**
   * Turns the "Offer received" box into an expander showing the recorded offer (Lloyd, Aug 18:
   * "can you make it such that the box is expandable and shows the offer").
   *
   * Opt-in, and needs the case guid, because this same bar is shown to clients and partners on the
   * shared progress page. They see the step; they do not see the terms, and nothing is fetched for
   * them. Offers load on first expand rather than with the bar — a case list renders many of these.
   */
  showOfferDetail?: boolean;
  ticketGuid?: string;
  /**
   * Facts recorded on the case that fill in steps sitting off the eight-stage spine — today, the
   * two discovery dates (Lloyd, Aug 19).
   *
   * Optional, and an omitted key reads as "not yet". The client-facing progress page passes none of
   * this: a client seeing DS and DR permanently empty is accurate, where inventing a value would
   * not be.
   */
  facts?: JourneyFacts;
  /**
   * The dates behind the boxes a person stamps (see STAMPABLE_FACTS). With `onSaveStamp`, those
   * boxes become clickable and open a date dialog. Omit both for a read-only bar — the client and
   * partner progress pages must never offer this.
   */
  stamps?: JourneyStamps;
  onSaveStamp?: (key: JourneyFactKey, date: string | null) => Promise<void>;
}

/**
 * How a single step is drawn.
 *
 * Three states, and the third is the one that matters. A tracked step is solid and behaves as it
 * always has. An UNTRACKED step is hollow and dashed: it is part of Lloyd's map, but nothing in the
 * system will ever fill it in. Drawing it solid-but-grey would be indistinguishable from "this case
 * has not got there yet", and that is precisely the confusion the hollow style exists to prevent.
 */
function stepSx(
  step: JourneyStep,
  current: number,
  showLabels: boolean,
  facts: JourneyFacts,
) {
  const stage = step.canonicalStage;
  // A fact-backed step is done when the case carries the fact, and never "current" — it records
  // something that happened, not where the case stands. Discovery coming back does not move a case
  // off pre-trial.
  const factDone = step.factKey !== undefined && facts[step.factKey] === true;
  const done =
    factDone ||
    (step.tracked && stage !== undefined && current >= 0 && stage < current);
  const isCurrent = step.tracked && stage !== undefined && stage === current;

  if (!step.tracked) {
    return {
      sx: {
        flex: showLabels ? '1 1 140px' : '0 0 auto',
        minWidth: showLabels ? 120 : 44,
        textAlign: 'center' as const,
        bgcolor: 'transparent',
        color: 'text.disabled',
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        border: '1px dashed',
        borderColor: 'grey.400',
        width: '100%',
      },
      done,
      isCurrent,
    };
  }

  return {
    sx: {
      flex: showLabels ? '1 1 140px' : '0 0 auto',
      minWidth: showLabels ? 120 : 44,
      textAlign: 'center' as const,
      bgcolor: isCurrent ? 'primary.main' : done ? 'primary.light' : 'grey.200',
      color: isCurrent || done ? 'primary.contrastText' : 'text.secondary',
      borderRadius: 1,
      px: 0.75,
      py: 0.25,
      fontWeight: isCurrent ? 700 : 400,
      border: isCurrent ? '2px solid' : '1px solid',
      borderColor: isCurrent ? 'primary.dark' : 'divider',
      width: '100%',
    },
    done,
    isCurrent,
  };
}

const CaseStatusTimeline: React.FC<CaseStatusTimelineProps> = ({
  statusName,
  currentStageIndex,
  onSelectStage,
  disabled = false,
  matterType,
  showOfferDetail = false,
  ticketGuid,
  facts = {},
  stamps = {},
  onSaveStamp,
}) => {
  const current = currentStageIndex ?? stageForStatus(statusName);
  // Abbreviations by default — Lloyd, Aug 18: "abbreviate the steps as much as possible, so we can
  // see the progression in one giant view", with the meaning one hover or one click away.
  const [showLabels, setShowLabels] = React.useState(false);

  const offerDetailEnabled = showOfferDetail && Boolean(ticketGuid);
  // The stage move the expander displaces, kept available inside the panel it opens.
  const offerStatus = statusForStage(OFFER_STAGE_INDEX);
  const offerSettable =
    offerDetailEnabled && Boolean(onSelectStage) && !disabled && offerStatus !== null && current !== OFFER_STAGE_INDEX;
  const [offerOpen, setOfferOpen] = React.useState(false);
  /**
   * Whether the three paths a case may take after an offer are on screen.
   *
   * Closed by default. Lloyd's four phase rows are what he asked to see at a glance; appeal, jury
   * trial and the county case are alternatives to one another, only one of which ever happens, and
   * drawing all three under every case is the clutter the four rows were meant to remove.
   *
   * The rows the case is actually on are the exception: they are drawn under the phases without
   * being asked for (30 Sep 2026, see outcomesTaken). Only the rest wait behind the link.
   */
  const [showOutcomes, setShowOutcomes] = React.useState(false);
  const taken = outcomesTaken(facts);
  const notTaken = JOURNEY_OUTCOMES.filter((branch) => !taken.includes(branch));
  const [stamping, setStamping] = React.useState<JourneyStep | null>(null);
  const [offers, setOffers] = React.useState<OfferRecord[] | null>(null);
  const [offersError, setOffersError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Fetched once, on first expand. Collapsing and reopening shows what was already loaded.
    if (!offerOpen || !ticketGuid || offers !== null) return;
    let cancelled = false;
    OfferPortalApi.listOffers(ticketGuid)
      .then((loaded) => {
        if (!cancelled) setOffers(loaded);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setOffersError(err instanceof Error ? err.message : 'Could not load the offer.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [offerOpen, ticketGuid, offers]);

  /**
   * One row of the chart. Phase rows and outcome rows draw identically — the only difference is
   * whether they are on screen without being asked for — so this is the same code for both.
   */
  const renderBranch = (branch: JourneyBranch) => (
    <Box
      key={branch.track}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, minWidth: 0 }}
    >
      <Tooltip arrow title={branch.note ?? ''}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: '0 0 150px', textAlign: 'right', pr: 0.5 }}
        >
          {branch.title}
        </Typography>
      </Tooltip>

      <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', flex: 1, minWidth: 0, py: 0.25 }}>
        {branch.steps.map((step) => {
          const { sx } = stepSx(step, current, showLabels, facts);
          const stage = step.canonicalStage;
          const isCurrent = step.tracked && stage !== undefined && stage === current;
          const targetStatus = stage !== undefined ? statusForStage(stage) : null;
          const settable =
            step.tracked && Boolean(onSelectStage) && targetStatus !== null && !isCurrent;
          // Filled in where its data lives, not by clicking the bar.
          const isFactStep = step.factKey !== undefined;
          const factDone = isFactStep && facts[step.factKey!] === true;
          const stampable =
            isFactStep && Boolean(onSaveStamp) && STAMPABLE_FACTS.includes(step.factKey!);
          const stampedOn = stampable ? stamps[step.factKey!] ?? null : null;

          // The offer step opens the offer instead of setting the stage. One click target, one
          // result — and the stage move it replaces is offered inside the panel, where it reads
          // as a decision rather than something a mis-click can do.
          const isOfferStep = offerDetailEnabled && step.id === 'OFFER_IN';

          const label = (
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
              {showLabels ? `${step.code} · ${step.label}` : step.code}
              {isOfferStep ? (offerOpen ? ' ▾' : ' ▸') : ''}
            </Typography>
          );

          const tooltipTitle = isOfferStep
            ? `${step.label} — click to show the offer`
            : !step.tracked
              ? `${step.label} — not tracked yet.${step.note ? ` ${step.note}` : ''}`
              : stampable
                ? `${step.label} — ${stampedOn ? `recorded for ${stampedOn.slice(0, 10)}` : 'not recorded yet'}. Click to ${stampedOn ? 'change' : 'record'} it.`
              : isFactStep
                ? `${step.label} — ${factDone ? 'recorded' : 'not recorded yet'}.${step.note ? ` ${step.note}` : ''}`
                : settable
                  ? `${step.label} — click to move here`
                  : step.label;

          return (
            <Tooltip key={step.id} title={tooltipTitle} arrow>
              <Box sx={{ flex: sx.flex, minWidth: sx.minWidth, display: 'flex' }}>
                {isOfferStep ? (
                  <ButtonBase
                    onClick={() => setOfferOpen((open) => !open)}
                    sx={{
                      ...sx,
                      cursor: 'pointer',
                      transition: 'filter 120ms',
                      '&:hover': { filter: 'brightness(0.92)' },
                    }}
                  >
                    {label}
                  </ButtonBase>
                ) : stampable ? (
                  <ButtonBase
                    disabled={disabled}
                    onClick={() => setStamping(step)}
                    sx={{
                      ...sx,
                      cursor: disabled ? 'default' : 'pointer',
                      transition: 'filter 120ms',
                      '&:hover': disabled ? {} : { filter: 'brightness(0.92)' },
                    }}
                  >
                    {label}
                  </ButtonBase>
                ) : settable ? (
                  <ButtonBase
                    disabled={disabled}
                    onClick={() => onSelectStage!(targetStatus!, step.label)}
                    sx={{
                      ...sx,
                      cursor: disabled ? 'default' : 'pointer',
                      transition: 'filter 120ms',
                      '&:hover': disabled ? {} : { filter: 'brightness(0.92)' },
                    }}
                  >
                    {label}
                  </ButtonBase>
                ) : (
                  <Box sx={sx}>{label}</Box>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );

  if (matterType === 'NON_TRAFFIC') {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
        Non-traffic matter — the traffic progression does not apply. Tracked in Clio.
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={() => setShowLabels((v) => !v)}
        >
          {showLabels ? 'Show codes only' : 'Show what each code means'}
        </Link>
        <Tooltip
          arrow
          title={
            'A dashed box is a step in the process that nothing records yet, so it can never fill ' +
            'in on its own. Each one is a decision point that needs capturing before it can be ' +
            'automated.'
          }
        >
          <Chip
            size="small"
            variant="outlined"
            label={`${JOURNEY_TRACKED_COUNT} of ${JOURNEY_STEP_COUNT} steps tracked`}
          />
        </Tooltip>
      </Box>

      {JOURNEY_PHASES.map(renderBranch)}

      {/*
        The paths after an offer (Lloyd, Aug 19). His four rows are the ones every case has; these
        are alternatives. The ones this case is on are shown; the rest stay behind the link.
      */}
      {taken.length > 0 && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', ml: '158px', mt: 0.5, mb: 0.5 }}
          >
            The path this case is on, from what has been recorded.
          </Typography>
          {taken.map(renderBranch)}
        </>
      )}

      {notTaken.length > 0 && (
        <>
          <Box sx={{ ml: '158px', mt: 0.5 }}>
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              onClick={() => setShowOutcomes((open) => !open)}
            >
              {showOutcomes
                ? taken.length > 0
                  ? 'Hide the other paths'
                  : 'Hide what happens after the offer is answered'
                : taken.length > 0
                  ? `The other paths — ${notTaken.map((b) => b.title.replace(/^If rejected — /, '')).join(', ')}`
                  : 'What happens after the offer is answered — appeal, jury trial, county'}
            </Link>
          </Box>

          <Collapse in={showOutcomes} unmountOnExit>
            {taken.length === 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', ml: '158px', mt: 0.5, mb: 0.5 }}
              >
                Nothing on this case says which path it is on yet. A row opens by itself once an
                appeal bond, a jury trial request or a county case is recorded.
              </Typography>
            )}
            {notTaken.map(renderBranch)}
          </Collapse>
        </>
      )}

      {onSaveStamp && (
        <JourneyStampDialog
          open={stamping !== null}
          label={stamping?.label ?? ''}
          current={stamping?.factKey ? stamps[stamping.factKey] ?? null : null}
          onClose={() => setStamping(null)}
          onSave={(date) => onSaveStamp(stamping!.factKey!, date)}
        />
      )}

      {offerDetailEnabled && offerOpen && (
        <Box sx={{ mt: 1, ml: '158px', maxWidth: 560 }}>
          {offersError ? (
            <Typography variant="body2" color="error">
              {offersError}
            </Typography>
          ) : offers === null ? (
            <Typography variant="body2" color="text.secondary">
              Loading the offer…
            </Typography>
          ) : offers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No offer has been recorded on this case yet. Record one on the Offer tab — including
              offers made over the phone or at a hearing.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {offers.map((offer, index) => (
                <OfferSummary key={offer.guid} offer={offer} superseded={index > 0} />
              ))}
            </Box>
          )}

          {offerSettable && (
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              sx={{ mt: 1 }}
              onClick={() => onSelectStage!(offerStatus!, 'Offer received from the court')}
            >
              Move this case to “offer received”
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CaseStatusTimeline;
